import { AbsgArrayHelper, CacheService, HttpExtra } from '@absg/angular-common';
import {
  AssignedProductDetail, CachedResultList1, DispenseType, FormularyApi, FormularyODataApi, Group,
  ItemType, LookupODataApi, ODataResultProductDetail, PracticeProductsRequest, ProductDetail, UnitOfMeasure,
} from '@absg/api-formulary';
import { CachedRequestList1 } from '@absg/api-formulary/model/cachedRequestList1';
import { CacheKeys, EnvironmentService, SharedFormularyService } from '@absg/nim-client-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseStateManagementService } from './state-management.service';

@Injectable({ providedIn: 'root' })
export class FormularyManagementService extends BaseStateManagementService implements OnDestroy {
  constructor(
    private lookupODataApi: LookupODataApi,
    private formularyApi: FormularyApi,
    private formularyODataApi: FormularyODataApi,
    private sharedFormularyService: SharedFormularyService,
    cacheService: CacheService,
    private environmentService: EnvironmentService,
  ) {
    super(cacheService);
  }

  private getProductsByIds(supplierProductInclusionIdList: Array<number>, extraHttpOptions?: HttpExtra): Observable<ProductDetail[]> {
    const cacheTime = this.getCacheTime(CacheKeys.productDetails);
    const request = {
      data: supplierProductInclusionIdList,
      clientCacheDateTime: cacheTime,
    } as CachedRequestList1;

    return this.formularyApi.getPracticeAssignedFormularyProductsCachedBeta(request, extraHttpOptions).pipe(
      catchError((response: HttpErrorResponse) => {
        this.handleErrorResponse(response);
        return of({ clientCacheDateTime: cacheTime, useCache: true, data: [] } as CachedResultList1); // return cache when erroring - example: no internet
      }),
      map((results) => this.handleCachingServiceCall(CacheKeys.productDetails, results)),
      map((results) => {
        const details = results?.data as ProductDetail[];

        if (!details || supplierProductInclusionIdList.length === details.length) {
          return details;
        }

        // if we're asking for a subset of the cache then only return those values
        const filtered = AbsgArrayHelper.filter(details, f => AbsgArrayHelper.some(supplierProductInclusionIdList, s => s === f.supplierProductInclusionId));
        return filtered;
      }),
      map((data) => this.sharedFormularyService.sortFormularyItems(data)),
    );
  }

  private syncProductGroupProducts(items: ProductDetail[]): ProductDetail[] {
    AbsgArrayHelper.forEach(items, item => {
      if ((!item.productGroupProducts || item.productGroupProducts.length === 0) && item.productGroupProduct) {
        item.productGroupProducts = [item.productGroupProduct];
      }
    });
    return items;
  }

  searchFormularyItemsBasedOnInventory(supplierProductInclusionIdList: Array<number>, searchString: string): Observable<ProductDetail[]> {
    // Step 1: Pass query to the formulary service
    // Step 2: Map formulary results to InventoryItem
    // Step 3: Map/Add Inventory results to InventoryItem
    // Step 4: Map/Add Position results to InventoryItem
    searchString = searchString.trim();
    if (searchString.length > 0) {
      const filterCriteria = `((startswith(displayName,'${searchString}') eq true) or ` +
        `(startswith(alternateName,'${searchString}') eq true) or ` +
        `(startswith(itemId,'${searchString}') eq true) or ` +
        `(startswith(unitNdc,'${searchString}') eq true))`;

      const httpParams = { $filter: `(${filterCriteria})`, $expand: 'ProductGroupProduct' };
      return this.formularyODataApi.getPracticeAssignedFormularyProductsOData({ SupplierProductInclusionIds: supplierProductInclusionIdList } as PracticeProductsRequest, { search: httpParams })
        .pipe(map((response: ODataResultProductDetail) => {
          const items = this.sharedFormularyService.sortFormularyItems(response.value as ProductDetail[]);
          return this.syncProductGroupProducts(items);
        }),
          catchError((response: HttpErrorResponse) => {
            this.handleErrorResponse(response);
            return of(void 0);
          }),
        );
    } else {
      if (this.environmentService.usingStateManagement()) {
        return this.getProductsByIds(supplierProductInclusionIdList);
      } else {
        const httpParams = { $expand: 'ProductGroupProduct' };
        return this.formularyODataApi.getPracticeAssignedFormularyProductsOData({ SupplierProductInclusionIds: supplierProductInclusionIdList } as PracticeProductsRequest, { search: httpParams })
          .pipe(map((response: ODataResultProductDetail) => {
            const items = this.sharedFormularyService.sortFormularyItems(response.value as ProductDetail[]);
            return this.syncProductGroupProducts(items);
          }),
            catchError((response: HttpErrorResponse) => {
              this.handleErrorResponse(response);
              return of(void 0);
            }),
          );
      }
    }
  }

  getItemTypes(extraHttpOptions?: HttpExtra): Observable<Array<ItemType>> {
    const cacheValue = this.getCache(CacheKeys.itemTypes, true);
    if (cacheValue) { return of(cacheValue); }

    return this.lookupODataApi.getItemTypesOData(extraHttpOptions).pipe(
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      map((results) => results?.value as Array<ItemType>),
      map((results) => {
        if (results) {
          this.setCache(CacheKeys.itemTypes, results, true);
        }
        return results;
      }),
    );
  }

  getGroups(extraHttpOptions?: HttpExtra): Observable<Array<Group>> {
    const cacheValue = this.getCache(CacheKeys.groups, true);
    if (cacheValue) { return of(cacheValue); }

    return this.lookupODataApi.getGroupsOData(extraHttpOptions).pipe(
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      map((results) => results?.value as Array<Group>),
      map((results) => {
        if (results) {
          this.setCache(CacheKeys.groups, results, true);
        }
        return results;
      }),
    );
  }

  getUnitOfMeasures(extraHttpOptions?: HttpExtra): Observable<Array<UnitOfMeasure>> {
    const cacheValue = this.getCache(CacheKeys.unitOfMeasures, true);
    if (cacheValue) { return of(cacheValue as Array<UnitOfMeasure>); }

    return this.lookupODataApi.getUnitOfMeasuresOData(extraHttpOptions).pipe(
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      map((results) => results?.value as Array<UnitOfMeasure>),
      map((results) => {
        if (results) {
          this.setCache(CacheKeys.unitOfMeasures, results, true);
        }
        return results;
      }),
    );
  }

  getDispenseTypes(): Observable<Array<DispenseType>> {
    const cacheValue = this.getCache(CacheKeys.dispenseTypes, true);
    if (cacheValue) { return of(cacheValue); }

    return this.lookupODataApi.getDispenseTypesOData().pipe(
      catchError((response: HttpErrorResponse) => this.handleErrorResponse(response)),
      map((results) => results?.value as Array<DispenseType>),
      map((results) => {
        if (results) {
          this.setCache(CacheKeys.dispenseTypes, results, true);
        }
        return results;
      }),
    );
  }

  removeProductCacheTimeKey(): void {
    this.removeCacheTime(CacheKeys.productDetails, true);
  }

  // TODO: When use state is normalized, remove and replace Clinic Charge service to just searchFormularyItemsBasedOnInventory
  getProductDetailsBySupplierProductInclusionIds(ids: number[]): Observable<ProductDetail[]> {
    if (this.environmentService.usingStateManagement()) {
      return this.getProductsByIds(ids);
    } else {
      return this.formularyApi.productDetailsBySupplierProductInclusionIds(ids);
    }
  }

  // TODO: When use state is normalized, remove and replace Clinic Charge service to just searchFormularyItemsBasedOnInventory
  getProductInclusionListById(ids: number[]): Observable<ProductDetail[]> {
    if (this.environmentService.usingStateManagement()) {
      return this.getProductsByIds(ids);
    } else {
      return this.formularyApi.getProductInclusionListById(ids);
    }
  }

  // TODO: When use management is global - remove and replace Manage Position Service piece to getProductsByIds only
  getPracticeAssignedProductDetails(supplierProductInclusionIds: number[]): Observable<AssignedProductDetail[]> {
    if (this.environmentService.usingStateManagement()) {
      return this.getProductsByIds(supplierProductInclusionIds).pipe(map((productDetails) => this.toAssignedProductDetails(productDetails)));
    } else {
      return this.formularyApi.getPracticeAssignedProductDetails(supplierProductInclusionIds);
    }
  }

  private toAssignedProductDetails(productDetails: ProductDetail[]): AssignedProductDetail[] {
    const list: AssignedProductDetail[] = [];
    AbsgArrayHelper.each(productDetails, detail => {
      const item = {
        productId: detail.productId,
        alternateName: detail.alternateName,
        genericName: detail.genericName,
        displayName: detail.displayName,
        isPracticeFavorite: detail.isPracticeFavorite,
        productTypeId: detail.productTypeId,
        packageNdc: detail.packageNdc,
        unitNdc: detail.unitNdc,
        jCode: detail.jCode,
        dUomDisplay: detail.dispenseUnitOfMeasureDisplay,
        vUomDisplay: detail.volumeUnitOfMeasureDisplay,
        pUomDisplay: detail.purchaseUnitOfMeasureDisplay,
        iUomDisplay: detail.inventoryUnitOfMeasureDisplay,
        dispenseTypeId: detail.dispenseTypeId,
        refrigerated: detail.refrigerated,
        singleDoseVial: detail.singleDoseVial,
        formularyTypeId: detail.formularyTypeId,
        supplierProductId: detail.supplierProductId,
        supplierProductInclusionId: detail.supplierProductInclusionId,
        altJCode: detail.altJCode,
        isDiscontinued: detail.isDiscontinued,
        itemId: detail.itemId,
        iFactor: detail.inventoryUnitOfMeasureConversionFactor,
        dFactor: detail.dispenseUnitOfMeasureConversionFactor,
        vFactor: detail.volumeUnitOfMeasureConversionFactor,
        pFactor: detail.purchaseUnitOfMeasureConversionFactor,
        isAuditFavorite: detail.isAuditFavorite,
        meiParentId: detail.meiParentId,
        supplierPartyId: detail.supplierPartyId,
        is340B: detail.is340B,
        isBuyIn: detail.isBuyIn,
        pUnitSize: detail.purchaseUnitOfMeasureSize,
        tradeName: detail.tradeName,
        gcnSequenceNumber: detail.gcnSequenceNumber,
        isItem505b2: detail.isItem505b2,
      } as AssignedProductDetail;
      list.push(item);
    });
    return list;
  }
}
